import { any, string, array, bool } from 'prop-types';
import dynamic from 'next/dynamic';
import type { GetServerSidePropsContext } from 'next';

import { getLang, getMarket } from 'utils/helpers';
import { initCMS } from 'utils/cms';
import { getStoresByCoords, getStoresByCountry, getStore, getAllStores, getAllStoresByCountry, getAllStoresByCity } from 'actions/shops';
import { isNull, normalizeUrl, normalizeString, removeStrangeCharacters, hasNonAlphaCharacters } from 'utils/StringUtils';
import { geoSearch } from 'actions/geo-search';
import { getDomainValue } from 'utils/domain';
import { STORE_TYPES } from 'utils/constants/stores';
import type { TStore } from 'types/stores';
import { parseLocale } from 'hooks';

const NnormalStores = dynamic(() => import('components/stores/nnormal-stores').then((mod) => mod.NnormalStores));
const CamperStores = dynamic(() => import('components/stores/camper-stores').then((mod) => mod.CamperStores));
const LabStores = dynamic(() => import('components/stores/lab-stores').then((mod) => mod.LabStores));

export default function ShopLocator({
  defaultProps,
  preloadedStores,
  preloadedSearch,
  preloadedCities,
  countryProp,
  placeProp,
  urlCanonical,
  preloadedStoresByTypology,
  preloadedFilterBy,
  firstStep,
  shoppingLinks,
  allStoresByCountry,
  allStoresByCity,
}: TODO) {
  return getDomainValue({
    camper: (
      <CamperStores
        defaultProps={defaultProps}
        preloadedStores={preloadedStores}
        preloadedSearch={preloadedSearch}
        preloadedCities={preloadedCities}
        countryProp={countryProp}
        placeProp={placeProp}
        urlCanonical={urlCanonical}
        preloadedStoresByTypology={preloadedStoresByTypology}
        preloadedFilterBy={preloadedFilterBy}
        firstStep={firstStep}
        shoppingLinks={shoppingLinks}
        allStoresByCountry={allStoresByCountry}
        allStoresByCity={allStoresByCity}
      />
    ),
    nnormal: <NnormalStores defaultProps={defaultProps} stores={preloadedStores} />,
    camperlab: <LabStores defaultProps={defaultProps} stores={preloadedStores} />,
  });
}

ShopLocator.propTypes = {
  footerProps: any,
  tnmProps: any,
  preloadedStores: array,
  preloadedSearch: string,
  preloadedCities: array,
  allStoresByCountry: array,
  allStoresByCity: array,
  countryProp: string,
  placeProp: string,
  urlCanonical: string,
  preloadedStoresByTypology: array,
  preloadedFilterBy: array,
  firstStep: bool,
  shoppingLinks: array,
};

ShopLocator.i18nPage = 'Shoplocator';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { locale } = parseLocale(context.locale ?? '');
  const { location } = context.params as { location: string[] };
  const { defaultProps } = await initCMS({ context, locale });
  delete defaultProps.ribbon;
  if (
    getDomainValue({
      camper: false,
      nnormal: true,
      camperlab: true,
    })
  ) {
    let stores = (await getAllStores({ context, profile: locale, repairProgram: false })) as TStore[];
    stores = stores.map((store) => ({ ...store, url: '' }));
    return {
      props: {
        defaultProps,
        locale,
        preloadedStores: stores,
      },
    };
  }

  let preloadedStores: TODO = [];
  let preloadedSearch: TODO = '';
  let preloadedCities: TODO = [];
  let countryProp = null;
  let placeProp: TODO = null;
  let firstStep = false;
  const countryName = defaultProps.footerProps?.countryName ?? 'España';
  const { currentTienda, filter } = context.query;
  let { currentSearch }: TODO = context.query;

  if (currentTienda) {
    // hacer petición a tienda para sacar los fragmentos de la url
    const store = (await getStore({
      context,
      profile: locale,
      id: currentTienda as TODO,
    })) as TStore & { url?: string };

    if (store.url) {
      return {
        redirect: {
          destination: `/${locale}/shops${store.url}`,
          permanent: false,
        },
      };
    }

    // hay que decidir qué hacer al no encontrar store (404, /shops, /home, etc)
    return {
      notFound: true,
    };
  }

  const props = {
    defaultProps,
    locale,
    preloadedStores,
    preloadedSearch,
    preloadedCities,
  };

  let isCountry = null;
  let mustSearch = true;
  let nominatimResult = null;
  // if we have a city we need to also search the country to receive the translations of the country name
  let countryOfCity = null;

  const searchParams: { addressdetails: string; namedetails: string; q?: string } = { addressdetails: '1', namedetails: '1' };

  let apiParams = [context, getLang(locale)];

  if (!currentSearch && location === undefined) {
    currentSearch = countryName.replace(/ñ/g, 'n');
    firstStep = true;
  }
  if (currentSearch) {
    const resultByCountry = await getStoresByCountry(null, locale, currentSearch, false);

    if (
      resultByCountry &&
      typeof resultByCountry === 'object' &&
      'stores' in resultByCountry &&
      resultByCountry.stores !== undefined &&
      resultByCountry.stores !== null &&
      Array.isArray(resultByCountry.stores) &&
      resultByCountry.stores.length > 0
    ) {
      const { stores: newStores, cities: newCities } = resultByCountry as { stores: TStore[]; cities?: any[] };
      isCountry = true;
      preloadedStores = newStores;
      preloadedCities = newCities || null;
      preloadedSearch = currentSearch;
      countryProp = currentSearch;

      if (newCities !== undefined) {
        placeProp = currentSearch;
      }
      mustSearch = false;
    } else {
      searchParams.q = currentSearch;

      try {
        nominatimResult = await geoSearch({ query: searchParams, language: getLang(locale) });
      } catch (e) {
        console.error('nominatim error', e);
        return {
          props,
        };
      }
    }
  } else if (location && location.length > 0) {
    const [country, city] = location as TODO;
    isCountry = location.length === 1;

    if (isCountry) {
      searchParams.q = country.replace(/_/g, ' ');
    } else {
      const parsedCity = city.replace(/_/g, ' ');
      countryOfCity = country.replace(/_/g, ' ');
      searchParams.q = parsedCity;
    }
  }
  let urlCanonical = null;
  if (mustSearch) {
    if (!currentSearch || nominatimResult === null) {
      const searchResults = await geoSearch({ query: searchParams, language: getLang(locale) });
      nominatimResult = searchResults;
    }
    const [firstMatch] = nominatimResult;

    // Si no hay resultados, no es una página válida
    if (isNull(firstMatch)) {
      return {
        notFound: true,
      };
    }

    const { lat, lon, namedetails, address, boundingbox }: TODO = firstMatch;

    const { city, country, province, state, county, administrative } = address;
    let { country_code } = address;
    const localeNameKey = `name:${getLang(locale)}`;
    const { name, [localeNameKey]: localeName } = namedetails;

    if (isCountry === null) {
      isCountry = country_code && !administrative && !state && !county && !province && !city;
    }
    // metemos este if porque cuando la query es hong kong nominatim nos dice que su pais es china, y luego el api nos trae tiendas de china en lugar de las de hong kong
    if (
      isCountry &&
      country_code === 'cn' &&
      searchParams.q !== null &&
      searchParams.q !== undefined &&
      ['hong-kong', 'hong kong'].includes(searchParams.q.toLowerCase())
    ) {
      country_code = 'hk';
    }
    const apiMethod = isCountry ? getStoresByCountry : getStoresByCoords;
    apiParams = isCountry ? [...apiParams, country_code] : [...apiParams, lat, lon];

    if (!isCountry && boundingbox) {
      const [south, north, west, east] = boundingbox;
      apiParams = [context, getLang(locale), country_code, south, west, north, east];
    }

    const searchResult = await (apiMethod as any)(...apiParams);
    console.log({ searchResult });
    if (searchResult) {
      const { stores } = searchResult as { stores: TStore[]; cities?: any[] };
      preloadedSearch = localeName || name;
      preloadedStores = stores;

      if (isCountry) {
        preloadedCities = (searchResult as { cities?: any[] }).cities || null;
      }

      const translatedCountryOfCity = countryOfCity && firstMatch ? firstMatch.address?.country : null;

      // if (translatedCountryOfCity && hasNonAlphaCharacters(removeStrangeCharacters(translatedCountryOfCity))) {
      //   translatedCountryOfCity = nominatimCountryResult[0].namedetails['name:en'];
      // }
      // eslint-disable-next-line no-nested-ternary
      countryProp =
        translatedCountryOfCity !== null ? translatedCountryOfCity
        : isCountry ? localeName || name
        : country;

      placeProp = !isCountry ? localeName || name : administrative || state || county || province || city || null;

      urlCanonical = decodeURI(context.resolvedUrl);
      if (urlCanonical.includes('?')) {
        urlCanonical = urlCanonical.split('?').shift();
      }

      if (placeProp && countryProp) {
        const countryPropForCanonicalUrl = countryProp.toLocaleLowerCase();
        const countryUrl = location && location.length ? location[0] : country;
        const placeUrl = location && location.length ? location[1] : placeProp;

        if (countryUrl !== countryPropForCanonicalUrl && !hasNonAlphaCharacters(countryPropForCanonicalUrl)) {
          urlCanonical = urlCanonical?.replace(countryUrl, countryPropForCanonicalUrl);
        }
        if (placeUrl !== placeProp && !hasNonAlphaCharacters(placeProp)) {
          urlCanonical = urlCanonical?.replace(placeUrl, placeProp);
        }
      }
      const urlCanonicalSplitted = urlCanonical?.split('/');
      if (urlCanonicalSplitted?.length === 3 && country && placeProp) {
        // falta el pais en la url (shops/barcelona => shops/espana/barcelona)
        urlCanonical = `/shops/${country.toLocaleLowerCase()}/${urlCanonicalSplitted.pop()}`;
      }
      if (urlCanonicalSplitted?.length === 4 && placeProp && stores.length > 0) {
        // priorizamos la ciudad que viene de nuestra api para la urlcanonical
        urlCanonicalSplitted.pop();
        const closestStore = stores.filter((store) => normalizeString(placeProp).toLowerCase().includes(normalizeString(store.cityName).toLowerCase()));
        const storeCityName = closestStore.length > 0 ? closestStore[0]?.cityName : stores[0]?.cityName;
        urlCanonical = `${urlCanonicalSplitted.join('/')}/${normalizeString(storeCityName).toLowerCase()}`;
      }
    }
  }
  let allStoresByCountry: any[] = [];
  if ((countryProp === null && placeProp === null) || firstStep) {
    try {
      allStoresByCountry = (await getAllStoresByCountry(locale)) as any[];
    } catch (rsError) {
      console.error(rsError);
    }
  }
  let allStoresByCity: any[] = [];
  if (countryProp !== null && !placeProp && !firstStep) {
    let market = getMarket(locale);
    let marketByNominatimResult = null;

    if (market === 'XI') {
      market = 'GB';
    }

    if (nominatimResult) {
      const [firstMatch] = nominatimResult;
      const { address } = firstMatch as TODO;
      const { country_code } = address;
      if (country_code && country_code.toUpperCase() !== market) {
        marketByNominatimResult = country_code.toUpperCase();
      }
      if (urlCanonical?.split('/').length === 4 && firstMatch?.namedetails?.int_name) {
        // es decir tiene ciudad en la url despues del pais '/shops/deutschland/munich'
        const urlCanonicalSplitted = urlCanonical.split('/');
        urlCanonicalSplitted.pop();
        urlCanonical = `${urlCanonicalSplitted.join('/')}/${normalizeString(firstMatch?.namedetails?.int_name).toLocaleLowerCase()}`;
      }
    }
    try {
      allStoresByCity = (await getAllStoresByCity(locale, marketByNominatimResult || market)) as any[];
    } catch (rsError) {
      console.error(rsError);
    }
  }
  const validFilterTypes = STORE_TYPES.map((store) => store.type);
  let preloadedStoresByTypology = null;
  const preloadedFilterBy = [];
  if (validFilterTypes.includes(filter as TODO)) {
    preloadedStoresByTypology = preloadedStores.filter((store: TODO) => store.tipologia === filter && store.latitude && store.longitude);
    preloadedFilterBy.push(`tipologia: ${filter}`);
  }
  const menuTypes = ['W', 'M', 'K', 'C'];
  const shoppingLinks = defaultProps?.tnmProps?.desktop
    ?.find((menu: TODO) => menu.client === 'camper')
    ?.menus?.filter((menu: TODO) => menuTypes.includes(menu.menuType));
  const womenLink = shoppingLinks?.filter((menu: TODO) => menu.menuType === 'W').shift();
  if (womenLink) {
    const accessoriesLink = womenLink.groups?.filter((menu: TODO) => menu.tracking.includes('accessories'))?.shift();
    const apparelLink = womenLink.groups?.filter((menu: TODO) => menu.tracking.includes('apparel'))?.shift();
    let camperlabLink = null;
    if (shoppingLinks[shoppingLinks.length - 1].menuType === 'C') {
      camperlabLink = shoppingLinks.pop();
    }
    if (accessoriesLink) {
      shoppingLinks.push(accessoriesLink);
    }
    if (apparelLink) {
      shoppingLinks.push(apparelLink);
    }
    if (camperlabLink) {
      shoppingLinks.push(camperlabLink);
    }
  }
  delete props.preloadedStores;
  delete props.preloadedSearch;
  delete props.preloadedCities;
  const preloadedNeededProps = {
    preloadedStores: preloadedStores.filter((store: TODO) => store.latitude && store.longitude),
    preloadedSearch: preloadedSearch.replace('spana', 'spaña'),
    preloadedCities,
  };
  return {
    props: {
      defaultProps,
      ...preloadedNeededProps,
      countryProp,
      placeProp,
      urlCanonical: urlCanonical ? normalizeUrl(urlCanonical.toLocaleLowerCase()) : '/shops',
      allStoresByCountry,
      allStoresByCity,
      preloadedStoresByTypology,
      preloadedFilterBy,
      firstStep,
      shoppingLinks: shoppingLinks || [],
    },
  };
}
